import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EMPLOYEES } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import TravelLedgerSection from "@/components/TravelLedgerSection";
import LoanLedgerSection from "@/components/LoanLedgerSection";
import LeaveTabSection from "@/components/LeaveTabSection";

const EMPTY_OPENING = { gross: 0, tax: 0, net: 0, as_at: "" };

const EMPTY = {
    name: "",
    position: "",
    employee_number: "",
    emp_code: "",
    labour_rate_kina: 0,
    dependants_declared: 0,
    tax_status: "resident_decl",
    recurring_allowances: { travel_kina: 0, housing_kina: 0, other_kina: 0 },
    bank: { bsb: "", account_number: "", account_name: "" },
    // Round 16 · optional multi-account splits. Empty = fall back to
    // the legacy `bank` above.
    bank_accounts: [],
    active: true,
    notes: "",
    rostered_start: "",
    rostered_finish: "",
    opening_ytd: null,
    annual_leave_balance_hours: null,
    sick_leave_balance_hours: null,
    loan_opening_balance_kina: null,
};

const TAX_OPTS = [
    { v: "resident_decl", label: "Resident — declaration lodged" },
    { v: "resident_no_decl", label: "Resident — no declaration (flat 42%)" },
    { v: "non_resident", label: "Non-resident" },
];

function Section({ title, children }) {
    return (
        <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-3 border-b border-neutral-200 pb-2">
                {title}
            </div>
            <div className="grid grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function Field({ label, tid, value, onChange, type = "text", step, span = 1, min }) {
    return (
        <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
            <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                {label}
            </span>
            <input
                data-testid={tid}
                type={type}
                step={step}
                min={min}
                value={value ?? ""}
                onChange={(e) => onChange(type === "number" ? e.target.valueAsNumber : e.target.value)}
                className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px] font-mono-money"
            />
        </label>
    );
}

// Nullable numeric field with an explicit "not supplied" state. Distinct from
// a zero — an employee with `null` AL has no imported figure, while zero
// would mean "no leave left". The toggle flips between the two.
function BalanceField({ label, tid, notSuppliedTid, value, onChange, span = 1 }) {
    const isNull = value === null || value === undefined || value === "";
    return (
        <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
            <span className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                <span>{label}</span>
                <button
                    type="button"
                    data-testid={notSuppliedTid}
                    onClick={() => onChange(isNull ? 0 : null)}
                    className={`text-[10px] tracking-[0.12em] px-2 py-0.5 rounded-[2px] border transition-colors ${
                        isNull
                            ? "border-amber-400 bg-amber-50 text-amber-700"
                            : "border-neutral-300 bg-white text-neutral-500 hover:border-neutral-500"
                    }`}
                >
                    {isNull ? "Not supplied" : "Clear"}
                </button>
            </span>
            <input
                data-testid={tid}
                type="number" step="0.01" min="0"
                value={isNull ? "" : value}
                placeholder={isNull ? "— not supplied —" : ""}
                onChange={(e) => onChange(e.target.value === "" ? null : e.target.valueAsNumber)}
                className={`kp-focus w-full border px-3 py-2 text-sm rounded-[3px] font-mono-money ${
                    isNull ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-white border-neutral-300"
                }`}
            />
        </label>
    );
}

export default function EmployeeDrawer({ open, employee, onClose, onSaved }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isNew = !employee;
    const [form, setForm] = useState(EMPTY);
    const [openingOn, setOpeningOn] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;
        const filled = employee
            ? {
                  ...EMPTY,
                  ...employee,
                  recurring_allowances: {
                      ...EMPTY.recurring_allowances,
                      ...(employee.recurring_allowances || {}),
                  },
                  bank: { ...EMPTY.bank, ...(employee.bank || {}) },
                  opening_ytd: employee.opening_ytd || null,
              }
            : EMPTY;
        setForm(filled);
        setOpeningOn(!!filled.opening_ytd);
    }, [open, employee]);

    if (!open) return null;

    const setField = (path, v) => {
        setForm((f) => {
            const next = { ...f };
            if (path.includes(".")) {
                const [a, b] = path.split(".");
                next[a] = { ...next[a], [b]: v };
            } else {
                next[path] = v;
            }
            return next;
        });
    };

    const save = async () => {
        if (!form.name?.trim()) {
            toast.error("Name is required");
            return;
        }
        setSaving(true);
        try {
            // Only admins can send opening_ytd. Build the payload without it for operators
            // (operators can't reach this button today either — belt-and-braces).
            let opening_ytd = null;
            if (isAdmin && openingOn) {
                const o = form.opening_ytd || EMPTY_OPENING;
                if (!o.as_at) {
                    toast.error("Set the 'as at' date for opening balances");
                    setSaving(false);
                    return;
                }
                opening_ytd = {
                    gross: Number(o.gross) || 0,
                    tax: Number(o.tax) || 0,
                    net: Number(o.net) || 0,
                    as_at: o.as_at,
                };
            }
            const payload = {
                ...form,
                labour_rate_kina: Number(form.labour_rate_kina) || 0,
                dependants_declared: Number(form.dependants_declared) || 0,
                recurring_allowances: {
                    travel_kina: Number(form.recurring_allowances.travel_kina) || 0,
                    housing_kina: Number(form.recurring_allowances.housing_kina) || 0,
                    other_kina: Number(form.recurring_allowances.other_kina) || 0,
                },
                emp_code: (form.emp_code || "").trim(),
                rostered_start: (form.rostered_start || "").trim() || null,
                rostered_finish: (form.rostered_finish || "").trim() || null,
                // Leave / loan opening — pass through the null-vs-number
                // distinction verbatim so "not supplied" round-trips.
                annual_leave_balance_hours:
                    form.annual_leave_balance_hours === "" || form.annual_leave_balance_hours == null
                        ? null : Number(form.annual_leave_balance_hours),
                sick_leave_balance_hours:
                    form.sick_leave_balance_hours === "" || form.sick_leave_balance_hours == null
                        ? null : Number(form.sick_leave_balance_hours),
                loan_opening_balance_kina:
                    form.loan_opening_balance_kina === "" || form.loan_opening_balance_kina == null
                        ? null : Number(form.loan_opening_balance_kina),
            };
            if (isAdmin) {
                // On  → send the object (server $set + stamps audit fields)
                // Off → send explicit null (server $unset + clears audit fields).
                //       Without this an accidental typo is stuck for the rest of the year.
                payload.opening_ytd = openingOn ? opening_ytd : null;
            } else {
                delete payload.opening_ytd;
            }
            // Never PATCH the audit fields — server owns them.
            delete payload.opening_ytd_set_by;
            delete payload.opening_ytd_set_at;
            if (isNew) {
                await api.post("/employees", payload);
                toast.success("Employee created");
            } else {
                await api.patch(`/employees/${employee.id}`, payload);
                toast.success("Employee saved");
            }
            onSaved?.();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!employee) return;
        if (!window.confirm(`Deactivate ${employee.name}? This is a soft delete — they'll be hidden from active lists.`)) return;
        try {
            await api.delete(`/employees/${employee.id}`);
            toast.success("Employee deactivated");
            onSaved?.();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
            />
            {/* Drawer */}
            <aside
                data-testid={EMPLOYEES.drawer}
                className="absolute right-0 top-0 h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col"
            >
                {/* Header */}
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">
                            {isNew ? "New employee" : "Employee profile"}
                        </div>
                        <div className="font-display font-bold text-2xl text-[color:var(--kp-charcoal)] mt-0.5">
                            {isNew ? "Add to master" : employee.name}
                        </div>
                    </div>
                    <button
                        data-testid={EMPLOYEES.drawerCloseBtn}
                        onClick={onClose}
                        className="p-1 text-neutral-500 hover:text-black"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    <Section title="Profile">
                        <Field label="Full name" tid={EMPLOYEES.drawerName} value={form.name} onChange={(v) => setField("name", v)} span={2} />
                        <Field label="Position" tid={EMPLOYEES.drawerPosition} value={form.position} onChange={(v) => setField("position", v)} span={2} />
                        <Field label="Employee no." tid={EMPLOYEES.drawerNumber} value={form.employee_number} onChange={(v) => setField("employee_number", v)} />
                        <Field label="Labour rate (K/hr)" tid={EMPLOYEES.drawerRate} value={form.labour_rate_kina} onChange={(v) => setField("labour_rate_kina", v)} type="number" step="0.01" min="0" />
                    </Section>

                    <Section title="Biometric & roster">
                        <label className="col-span-2 block">
                            <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                                Biometric emp code
                            </span>
                            <input
                                data-testid={EMPLOYEES.drawerEmpCode}
                                value={form.emp_code ?? ""}
                                onChange={(e) => setField("emp_code", e.target.value)}
                                className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px] font-mono-money"
                                placeholder="e.g. MKA92022 · leave blank for staff without a device"
                            />
                            <span className="text-[11px] text-neutral-500 mt-1 block leading-relaxed">
                                Used to match this person to rows in the raw biometric file.
                                Matching is by <strong>code, not name</strong>. Blank is a valid state
                                (Port Moresby staff etc.).
                            </span>
                        </label>
                        <Field
                            label="Rostered start (HH:MM)"
                            tid={EMPLOYEES.drawerRosteredStart}
                            value={form.rostered_start ?? ""}
                            onChange={(v) => setField("rostered_start", v)}
                        />
                        <Field
                            label="Rostered finish (HH:MM)"
                            tid={EMPLOYEES.drawerRosteredFinish}
                            value={form.rostered_finish ?? ""}
                            onChange={(v) => setField("rostered_finish", v)}
                        />
                    </Section>

                    <Section title="Tax status">
                        <label className="col-span-2 block">
                            <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2">
                                IRC status
                            </span>
                            <div data-testid={EMPLOYEES.drawerTaxStatus} className="flex flex-col gap-2">
                                {TAX_OPTS.map((o) => (
                                    <button
                                        key={o.v}
                                        type="button"
                                        onClick={() => setField("tax_status", o.v)}
                                        className={`text-left px-3 py-2 text-sm border rounded-[3px] transition-colors ${
                                            form.tax_status === o.v
                                                ? "border-[color:var(--kp-red)] bg-red-50"
                                                : "border-neutral-300 bg-white hover:border-neutral-400"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`inline-block w-2 h-2 ${
                                                    form.tax_status === o.v ? "bg-[color:var(--kp-red)]" : "bg-neutral-300"
                                                }`}
                                            />
                                            <span className="font-medium text-[color:var(--kp-charcoal)]">
                                                {o.label}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </label>
                        <Field
                            label="Dependants — declared (0..20)"
                            tid={EMPLOYEES.drawerDeps}
                            value={form.dependants_declared}
                            onChange={(v) => setField("dependants_declared", v)}
                            type="number" min="0" span={2}
                        />
                        <div className="col-span-2 text-[11px] text-neutral-500 -mt-2 leading-relaxed">
                            The <strong>truthful count</strong> for IRC Form S1. The rebate applied at
                            pay-run tops out at 3 — anything above 3 is stored and reported but doesn't
                            change fortnightly tax. Effective for rebate:{" "}
                            <span data-testid={EMPLOYEES.drawerDepsForRebate} className="font-mono-money">
                                {Math.min(Number(form.dependants_declared) || 0, 3)}
                            </span>
                        </div>
                    </Section>

                    <Section title="Recurring allowances (per fortnight)">
                        <Field label="Travel (K)" tid={EMPLOYEES.drawerTravel} value={form.recurring_allowances.travel_kina} onChange={(v) => setField("recurring_allowances.travel_kina", v)} type="number" step="0.01" min="0" />
                        <Field label="Housing (K)" tid={EMPLOYEES.drawerHousing} value={form.recurring_allowances.housing_kina} onChange={(v) => setField("recurring_allowances.housing_kina", v)} type="number" step="0.01" min="0" />
                        <Field label="Other (K)" tid={EMPLOYEES.drawerOther} value={form.recurring_allowances.other_kina} onChange={(v) => setField("recurring_allowances.other_kina", v)} type="number" step="0.01" min="0" span={2} />
                    </Section>

                    <Section title="Bank (legacy — single account)">
                        <Field label="BSB" tid={EMPLOYEES.drawerBsb} value={form.bank.bsb} onChange={(v) => setField("bank.bsb", v)} />
                        <Field label="Account no." tid={EMPLOYEES.drawerAcct} value={form.bank.account_number} onChange={(v) => setField("bank.account_number", v)} />
                        <Field label="Account name" tid={EMPLOYEES.drawerAcctName} value={form.bank.account_name} onChange={(v) => setField("bank.account_name", v)} span={2} />
                    </Section>

                    {/* Round 16 · multi-account bank splits. When populated,
                        the Westpac DE writer uses these instead of the legacy
                        single-account block above. One MUST be default; the
                        default account collects the remainder after per-run
                        dollar splits. Lodgement reference is Irene's rule —
                        max 18 chars, blank falls back to emp_code on emit. */}
                    <div className="mb-8" data-testid={EMPLOYEES.banksSection}>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-3 border-b border-neutral-200 pb-2 flex items-center justify-between">
                            <span>Bank accounts (for splits)</span>
                            <button
                                type="button"
                                data-testid={EMPLOYEES.banksAddBtn}
                                onClick={() => {
                                    const list = form.bank_accounts || [];
                                    const nid = `acc-${Date.now().toString(36)}`;
                                    const isFirst = list.length === 0;
                                    setField("bank_accounts", [
                                        ...list,
                                        { id: nid, bsb: "", account_number: "",
                                          account_title: "", nickname: "",
                                          is_default: isFirst, lodgement_reference: "" },
                                    ]);
                                }}
                                className="text-[10px] font-display font-semibold uppercase tracking-[0.14em] px-2 py-1 border rounded-[2px]"
                                style={{ borderColor: "var(--kp-red)", color: "var(--kp-red)" }}
                            >
                                + Add account
                            </button>
                        </div>
                        {(form.bank_accounts || []).length === 0 && (
                            <div className="text-[11px] text-neutral-500 italic">
                                No split accounts. Falls back to the single legacy account above.
                            </div>
                        )}
                        {(form.bank_accounts || []).map((acc, idx) => {
                            const lref = acc.lodgement_reference || "";
                            const lrefOver = lref.length > 18;
                            return (
                                <div
                                    key={acc.id || idx}
                                    data-testid={EMPLOYEES.bankRow(acc.id)}
                                    className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-neutral-100 last:border-b-0"
                                >
                                    <Field
                                        label="Nickname"
                                        tid={EMPLOYEES.bankNickname(acc.id)}
                                        value={acc.nickname}
                                        onChange={(v) => {
                                            const list = [...form.bank_accounts];
                                            list[idx] = { ...list[idx], nickname: v };
                                            setField("bank_accounts", list);
                                        }}
                                    />
                                    <div className="flex items-center gap-3">
                                        <label className="inline-flex items-center gap-2 text-[11px] text-neutral-600 cursor-pointer">
                                            <input
                                                data-testid={EMPLOYEES.bankDefault(acc.id)}
                                                type="radio"
                                                checked={!!acc.is_default}
                                                onChange={() => {
                                                    const list = form.bank_accounts.map((a, i) => ({ ...a, is_default: i === idx }));
                                                    setField("bank_accounts", list);
                                                }}
                                                className="accent-[color:var(--kp-red)]"
                                            />
                                            Default (gets remainder)
                                        </label>
                                        <button
                                            type="button"
                                            data-testid={EMPLOYEES.bankRemoveBtn(acc.id)}
                                            onClick={() => {
                                                const list = form.bank_accounts.filter((_, i) => i !== idx);
                                                // If we removed the default, promote the first remaining to default.
                                                if (acc.is_default && list.length > 0) list[0].is_default = true;
                                                setField("bank_accounts", list);
                                            }}
                                            className="text-[11px] text-neutral-500 hover:text-[color:var(--kp-red)] flex items-center gap-1"
                                        >
                                            <Trash2 size={12} /> Remove
                                        </button>
                                    </div>
                                    <Field
                                        label="BSB"
                                        tid={EMPLOYEES.bankBsb(acc.id)}
                                        value={acc.bsb}
                                        onChange={(v) => {
                                            const list = [...form.bank_accounts];
                                            list[idx] = { ...list[idx], bsb: v };
                                            setField("bank_accounts", list);
                                        }}
                                    />
                                    <Field
                                        label="Account no."
                                        tid={EMPLOYEES.bankAcct(acc.id)}
                                        value={acc.account_number}
                                        onChange={(v) => {
                                            const list = [...form.bank_accounts];
                                            list[idx] = { ...list[idx], account_number: v };
                                            setField("bank_accounts", list);
                                        }}
                                    />
                                    <Field
                                        label="Account title (32 chars)"
                                        tid={EMPLOYEES.bankTitle(acc.id)}
                                        value={acc.account_title}
                                        onChange={(v) => {
                                            const list = [...form.bank_accounts];
                                            list[idx] = { ...list[idx], account_title: v };
                                            setField("bank_accounts", list);
                                        }}
                                        span={2}
                                    />
                                    <label className="col-span-2 block">
                                        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1 flex items-center justify-between">
                                            <span>Lodgement reference (Irene's savings rule — optional, max 18)</span>
                                            <span
                                                data-testid={EMPLOYEES.bankLrefCount(acc.id)}
                                                className="font-mono-money text-[11px]"
                                                style={{ color: lrefOver ? "var(--kp-red)" : "var(--kp-muted)" }}
                                            >
                                                {lref.length}/18
                                            </span>
                                        </span>
                                        <input
                                            data-testid={EMPLOYEES.bankLref(acc.id)}
                                            type="text"
                                            maxLength={18}
                                            value={lref}
                                            onChange={(e) => {
                                                const list = [...form.bank_accounts];
                                                list[idx] = { ...list[idx], lodgement_reference: e.target.value };
                                                setField("bank_accounts", list);
                                            }}
                                            placeholder="e.g. MEMBER99 — blank falls back to employee code"
                                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px] font-mono-money"
                                            style={lrefOver ? { borderColor: "var(--kp-red)" } : {}}
                                        />
                                    </label>
                                </div>
                            );
                        })}
                    </div>

                    <Section title="Notes">
                        <label className="col-span-2 block">
                            <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                                Internal notes
                            </span>
                            <textarea
                                data-testid={EMPLOYEES.drawerNotes}
                                rows={3}
                                value={form.notes ?? ""}
                                onChange={(e) => setField("notes", e.target.value)}
                                className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]"
                            />
                        </label>
                    </Section>

                    {isAdmin && (
                        <div
                            data-testid={EMPLOYEES.drawerOpeningSection}
                            className="mb-8 border-l-2 pl-4 py-3 rounded-[2px]"
                            style={{ borderLeftColor: "var(--kp-red)", background: "#FFF7F6" }}
                        >
                            <div className="flex items-start justify-between mb-1">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-display" style={{ color: "var(--kp-red)" }}>
                                        Opening balances (prior system)
                                    </div>
                                    <div className="text-[11px] text-neutral-600 mt-1 leading-relaxed max-w-[420px]">
                                        Year-to-date figures carried in from Excel/previous payroll.
                                        These are <strong>not</strong> calculated by this app — enter them once at cut-over
                                        and payslips will show a correct combined YTD. Stops applying automatically on 1&nbsp;January
                                        of the following year.
                                    </div>
                                </div>
                                <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-neutral-600 font-display shrink-0 mt-1">
                                    <input
                                        type="checkbox"
                                        data-testid={EMPLOYEES.drawerOpeningToggle}
                                        checked={openingOn}
                                        onChange={(e) => {
                                            const on = e.target.checked;
                                            setOpeningOn(on);
                                            if (on && !form.opening_ytd) {
                                                setField("opening_ytd", { ...EMPTY_OPENING });
                                            }
                                        }}
                                        className="accent-[color:var(--kp-red)]"
                                    />
                                    On
                                </label>
                            </div>
                            {openingOn && (
                                <>
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        <Field
                                            label="Gross YTD (K)"
                                            tid={EMPLOYEES.drawerOpeningGross}
                                            value={form.opening_ytd?.gross ?? 0}
                                            onChange={(v) => setField("opening_ytd", { ...(form.opening_ytd || EMPTY_OPENING), gross: v })}
                                            type="number" step="0.01" min="0"
                                        />
                                        <Field
                                            label="Tax YTD (K)"
                                            tid={EMPLOYEES.drawerOpeningTax}
                                            value={form.opening_ytd?.tax ?? 0}
                                            onChange={(v) => setField("opening_ytd", { ...(form.opening_ytd || EMPTY_OPENING), tax: v })}
                                            type="number" step="0.01" min="0"
                                        />
                                        <Field
                                            label="Net YTD (K)"
                                            tid={EMPLOYEES.drawerOpeningNet}
                                            value={form.opening_ytd?.net ?? 0}
                                            onChange={(v) => setField("opening_ytd", { ...(form.opening_ytd || EMPTY_OPENING), net: v })}
                                            type="number" step="0.01" min="0"
                                        />
                                        <Field
                                            label="As at (last day covered)"
                                            tid={EMPLOYEES.drawerOpeningAsAt}
                                            value={form.opening_ytd?.as_at ?? ""}
                                            onChange={(v) => setField("opening_ytd", { ...(form.opening_ytd || EMPTY_OPENING), as_at: v })}
                                            type="date"
                                        />
                                    </div>
                                    {employee?.opening_ytd_set_by && (
                                        <div
                                            data-testid={EMPLOYEES.drawerOpeningAudit}
                                            className="text-[10px] text-neutral-500 mt-3 font-mono-money"
                                        >
                                            Set by {employee.opening_ytd_set_by} · {(employee.opening_ytd_set_at || "").slice(0, 10)}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {!isNew && (
                        <div className="text-[11px] text-neutral-400 font-mono-money">
                            id · {employee.id}
                        </div>
                    )}

                    {!isNew && isAdmin && employee?.id && (
                        <LeaveTabSection employeeId={employee.id} />
                    )}

                    {!isNew && isAdmin && (
                        <div data-testid={EMPLOYEES.drawerLeaveSection} className="mb-8">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-3 border-b border-neutral-200 pb-2">
                                Leave &amp; loan balances (imported / manual)
                            </div>
                            <div className="text-[11px] text-neutral-500 mb-4 leading-relaxed">
                                <strong className="text-neutral-700">Blank ≠ zero.</strong>{" "}
                                A blank balance surfaces as <em>not supplied</em> — that's different from
                                a zero balance, which says the employee has no leave left. The client's
                                workbook only has figures for 4 of 9 staff; the rest stay blank until the
                                bookkeeper produces a number.
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <BalanceField
                                    label="Annual leave — hours"
                                    tid={EMPLOYEES.drawerLeaveAlHours}
                                    notSuppliedTid={EMPLOYEES.drawerLeaveAlNotSupplied}
                                    value={form.annual_leave_balance_hours}
                                    onChange={(v) => setField("annual_leave_balance_hours", v)}
                                />
                                <BalanceField
                                    label="Sick leave — hours"
                                    tid={EMPLOYEES.drawerLeaveSlHours}
                                    notSuppliedTid={EMPLOYEES.drawerLeaveSlNotSupplied}
                                    value={form.sick_leave_balance_hours}
                                    onChange={(v) => setField("sick_leave_balance_hours", v)}
                                />
                                <BalanceField
                                    label="Loan opening balance (K)"
                                    tid={EMPLOYEES.drawerLoanOpeningKina}
                                    notSuppliedTid={EMPLOYEES.drawerLoanOpeningNotSupplied}
                                    value={form.loan_opening_balance_kina}
                                    onChange={(v) => setField("loan_opening_balance_kina", v)}
                                    span={2}
                                />
                            </div>
                            {(employee?.leave_adjustment_ledger || []).length > 0 && (
                                <div className="mt-4 border-l-2 border-neutral-300 pl-3">
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-display mb-2">
                                        Adjustment audit trail
                                    </div>
                                    <ul className="text-[11px] text-neutral-600 space-y-1 font-mono-money">
                                        {(employee.leave_adjustment_ledger || []).slice().reverse().slice(0, 8).map((a) => (
                                            <li key={a.id}>
                                                {a.date} · {a.leave_type} · {a.delta_hours > 0 ? "+" : ""}{a.delta_hours}h · {a.reason} —{" "}
                                                <span className="text-neutral-400">{a.recorded_by}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {!isNew && isAdmin && (
                        <>
                            <TravelLedgerSection employee={employee} onChanged={onSaved} />
                            <LoanLedgerSection employee={employee} onChanged={onSaved} />
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
                    {!isNew ? (
                        <button
                            data-testid={EMPLOYEES.drawerRemoveBtn}
                            onClick={remove}
                            className="inline-flex items-center gap-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-500 hover:text-[color:var(--kp-red)]"
                        >
                            <Trash2 size={13} /> Remove
                        </button>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black rounded-[3px]"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid={EMPLOYEES.drawerSaveBtn}
                            onClick={save}
                            disabled={saving}
                            className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-70"
                            style={{ background: "var(--kp-red)" }}
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </aside>
        </div>
    );
}
